/**
 * QCReviewQueue — Review QC findings with navigation and context.
 *
 * Shows one finding at a time with prev/next arrows and a scrollable
 * list for jumping to any finding. Actions: Fix Translation, Not an Issue.
 *
 * Route: /polyglot/qc/review/work?language=xx
 * API: GET /@polyglot/qc-review/next?language=xx&finding_id=N
 *      GET /@polyglot/qc-findings?language=xx&page_size=200 (list)
 *      PATCH /@polyglot/qc-findings/:id (disposition)
 *      POST /@polyglot/qc-findings/:id/apply (correction)
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import jwtDecode from 'jwt-decode';
import {
  Container,
  Header,
  Segment,
  Loader,
  Icon,
  Button,
  Label,
  Message,
  Breadcrumb,
  Table,
  Dropdown,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../api';

const ERROR_TYPE_OPTIONS = [
  { key: 'mistranslation', value: 'mistranslation', text: 'Mistranslation' },
  { key: 'wrong_meaning', value: 'wrong_meaning', text: 'Wrong meaning' },
  { key: 'incomplete', value: 'incomplete', text: 'Incomplete' },
  { key: 'typo', value: 'typo', text: 'Typo' },
  { key: 'wrong_number', value: 'wrong_number', text: 'Singular/plural' },
  { key: 'wrong_tense', value: 'wrong_tense', text: 'Wrong tense' },
  { key: 'grammar', value: 'grammar', text: 'Grammar' },
  { key: 'punctuation', value: 'punctuation', text: 'Punctuation' },
  { key: 'style', value: 'style', text: 'Style' },
  { key: 'missing_placeholder', value: 'missing_placeholder', text: 'Missing placeholder' },
  { key: 'markup_changed', value: 'markup_changed', text: 'Markup changed' },
  { key: 'other', value: 'other', text: 'Other' },
];

const severityColor = (sev) => {
  switch (sev) {
    case 'S4': return 'red';
    case 'S3': return 'orange';
    case 'S2': return 'yellow';
    default: return 'grey';
  }
};

export default function QCReviewQueue() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const language = params.get('language') || '';
  const platform = params.get('platform') || '';

  // Read-only mode for guest users
  const token = useSelector((state) => state.userSession?.token);
  const userId = token ? jwtDecode(token).sub : '';
  const readOnly = userId === 'bucharest26';

  // Finding list (all findings for this language, lightweight)
  const [findingList, setFindingList] = useState([]);
  const [listLoading, setListLoading] = useState(true);

  // Current finding detail + context
  const [currentId, setCurrentId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Correction state
  const [correction, setCorrection] = useState('');
  const [showCorrectionField, setShowCorrectionField] = useState(false);

  // Reclassify state
  const [reclassifyType, setReclassifyType] = useState('');

  // Action state
  const [acting, setActing] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);

  const activeRowRef = useRef(null);

  // 1. Fetch the finding list
  useEffect(() => {
    if (!language) return;
    setListLoading(true);
    const qp = new URLSearchParams();
    qp.set('language', language);
    if (platform) qp.set('platform', platform);
    qp.set('page_size', '500');

    fetchApi(`${getApiPath()}/@polyglot/qc-findings?${qp}`)
      .then((json) => {
        const items = json.items || [];
        setFindingList(items);
        setListLoading(false);
        // Auto-select first item
        if (items.length > 0 && !currentId) {
          setCurrentId(items[0].id);
        }
      })
      .catch((err) => {
        setError(err.message);
        setListLoading(false);
      });
  }, [language, platform]); // eslint-disable-line react-hooks/exhaustive-deps

  // 2. Fetch detail when currentId changes
  const fetchDetail = useCallback((findingId) => {
    if (!findingId || !language) return;
    setLoading(true);
    setActionMsg(null);
    setCorrection('');
    setShowCorrectionField(false);
    setReclassifyType('');

    fetchApi(`${getApiPath()}/@polyglot/qc-review/next?language=${language}&finding_id=${findingId}`)
      .then((json) => {
        setData(json);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [language]);

  useEffect(() => {
    if (currentId) fetchDetail(currentId);
  }, [currentId, fetchDetail]);

  // Scroll active row into view
  useEffect(() => {
    if (activeRowRef.current) {
      activeRowRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [currentId]);

  // Navigation
  const currentIdx = findingList.findIndex((f) => f.id === currentId);
  const canPrev = currentIdx > 0;
  const canNext = currentIdx < findingList.length - 1;

  const goPrev = () => {
    if (canPrev) setCurrentId(findingList[currentIdx - 1].id);
  };
  const goNext = () => {
    if (canNext) setCurrentId(findingList[currentIdx + 1].id);
  };

  // Save reclassification only (no disposition change, stays in queue)
  const handleReclassifyOnly = async () => {
    if (!data?.finding || !reclassifyType) return;
    setActing(true);
    setActionMsg(null);

    try {
      await fetchApi(`${getApiPath()}/@polyglot/qc-findings/${data.finding.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ disposition: 'confirmed', reviewed_error_type: reclassifyType }),
      });

      const label = ERROR_TYPE_OPTIONS.find((o) => o.value === reclassifyType)?.text;
      setActionMsg({ positive: true, text: `Reclassified as "${label}"` });

      // Update the finding in the list to reflect new type
      setFindingList((prev) =>
        prev.map((f) =>
          f.id === data.finding.id
            ? { ...f, error_type_label: label, error_type: reclassifyType }
            : f,
        ),
      );
      setReclassifyType('');
    } catch (err) {
      setActionMsg({ positive: false, text: err.message });
    } finally {
      setActing(false);
    }
  };

  // Actions
  const handleDisposition = async (disposition) => {
    if (!data?.finding) return;
    setActing(true);
    setActionMsg(null);

    try {
      const body = { disposition };
      if (reclassifyType) body.reviewed_error_type = reclassifyType;

      await fetchApi(`${getApiPath()}/@polyglot/qc-findings/${data.finding.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const labels = {
        acceptable: 'Dismissed — translation is OK',
        confirmed: 'Flagged — needs manual fix',
      };
      setActionMsg({ positive: true, text: labels[disposition] || disposition });

      // Remove from list and advance
      const removedIdx = currentIdx;
      const newList = findingList.filter((f) => f.id !== data.finding.id);
      setFindingList(newList);

      if (newList.length > 0) {
        const nextIdx = Math.min(removedIdx, newList.length - 1);
        setCurrentId(newList[nextIdx].id);
      } else {
        setCurrentId(null);
        setData(null);
      }
    } catch (err) {
      setActionMsg({ positive: false, text: err.message });
    } finally {
      setActing(false);
    }
  };

  const handleApplyCorrection = async () => {
    if (!data?.finding || !correction.trim()) return;
    setActing(true);
    setActionMsg(null);

    try {
      const applyResult = await fetchApi(`${getApiPath()}/@polyglot/qc-findings/${data.finding.id}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ custom_fix: correction.trim() }),
      });

      // Also send reclassification if set
      if (reclassifyType) {
        await fetchApi(`${getApiPath()}/@polyglot/qc-findings/${data.finding.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ disposition: 'confirmed', reviewed_error_type: reclassifyType }),
        });
      }

      const reclassMsg = reclassifyType ? ` (reclassified as ${ERROR_TYPE_OPTIONS.find(o => o.value === reclassifyType)?.text})` : '';
      // BUG-2026-197: the backend reconciles findings whose translation was
      // already fixed via another workflow — report that as the outcome.
      setActionMsg({
        positive: true,
        text: applyResult?.reconciled
          ? `Translation already fixed — finding reconciled${reclassMsg}`
          : `Correction applied${reclassMsg}`,
      });

      const removedIdx = currentIdx;
      const newList = findingList.filter((f) => f.id !== data.finding.id);
      setFindingList(newList);

      if (newList.length > 0) {
        const nextIdx = Math.min(removedIdx, newList.length - 1);
        setCurrentId(newList[nextIdx].id);
      } else {
        setCurrentId(null);
        setData(null);
      }
    } catch (err) {
      setActionMsg({ positive: false, text: err.message });
    } finally {
      setActing(false);
    }
  };

  if (!language) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Message warning>
          <Message.Header>No language selected</Message.Header>
          <p>Go to the <Link to="/polyglot/qc/review">review dashboard</Link> to pick a language.</p>
        </Message>
      </Container>
    );
  }

  const finding = data?.finding;
  const context = data?.context;
  const langName = context?.conventions?.language_name_english || language;

  return (
    <Container style={{ paddingTop: '2em', paddingBottom: '3em', maxWidth: '1100px' }}>
      <Helmet title={`Review ${langName}`} />

      <Breadcrumb style={{ marginBottom: '1em' }}>
        <Breadcrumb.Section as={Link} to="/polyglot">Polyglot</Breadcrumb.Section>
        <Breadcrumb.Divider icon="right angle" />
        <Breadcrumb.Section as={Link} to="/polyglot/qc/review">Review Queue</Breadcrumb.Section>
        <Breadcrumb.Divider icon="right angle" />
        <Breadcrumb.Section active>{langName}</Breadcrumb.Section>
      </Breadcrumb>

      {/* Navigation bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.8em',
        marginBottom: '1em', justifyContent: 'space-between',
      }}>
        <Button basic compact disabled={!canPrev} onClick={goPrev} title="Previous">
          <Icon name="chevron left" /> Prev
        </Button>
        <span style={{ fontSize: '0.95em', color: '#666' }}>
          {findingList.length > 0 ? `${currentIdx + 1} of ${findingList.length}` : 'No findings'}
        </span>
        <Button basic compact disabled={!canNext} onClick={goNext} title="Next">
          Next <Icon name="chevron right" />
        </Button>
      </div>

      {/* Error display */}
      {error && (
        <Message negative onDismiss={() => setError(null)} size="small">
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {/* Action feedback */}
      {actionMsg && (
        <Message
          positive={actionMsg.positive}
          negative={!actionMsg.positive}
          size="small"
          onDismiss={() => setActionMsg(null)}
        >
          {actionMsg.text}
        </Message>
      )}

      {/* Loading / empty */}
      {(loading || listLoading) && !finding && (
        <Loader active inline="centered" content="Loading..." />
      )}

      {!listLoading && findingList.length === 0 && (
        <Segment placeholder textAlign="center">
          <Header icon>
            <Icon name="check circle" color="green" />
            No pending findings for {langName}
          </Header>
          <Button as={Link} to="/polyglot/qc/review" primary>
            Pick another language
          </Button>
        </Segment>
      )}

      {/* --- The Card --- */}
      {finding && (
        <Segment raised style={{ opacity: loading ? 0.5 : 1 }}>
          {/* Header bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em' }}>
            <div>
              <Label color="blue" size="small">{finding.platform}</Label>
              <Label size="small">{finding.error_type_label || finding.error_type}</Label>
              {finding.risk_severity && (
                <Label color={severityColor(finding.risk_severity)} size="small">
                  {finding.risk_severity}
                </Label>
              )}
            </div>
            <Link
              to={`/polyglot/messages/${finding.message_id}?language=${language}`}
              style={{ fontSize: '0.9em' }}
            >
              Message #{finding.message_id} <Icon name="external" size="small" />
            </Link>
          </div>

          {/* English source */}
          <div style={{ marginBottom: '1.2em' }}>
            <div style={{ fontSize: '0.8em', textTransform: 'uppercase', color: '#888', letterSpacing: '0.05em', marginBottom: '0.3em' }}>
              English
            </div>
            <div style={{ fontSize: '1.3em', fontWeight: '600', lineHeight: '1.4' }}>
              {finding.english_source}
            </div>
          </div>

          {/* Current translation — the LIVE value (BUG-2026-197: current_value
              is an at-discovery snapshot, not the current translation) */}
          <div style={{ marginBottom: '1.2em' }}>
            <div style={{ fontSize: '0.8em', textTransform: 'uppercase', color: '#888', letterSpacing: '0.05em', marginBottom: '0.3em' }}>
              Current ({language})
            </div>
            <div style={{
              fontSize: '1.2em', lineHeight: '1.4',
              padding: '0.5em 0.7em',
              background: '#fff8f0',
              border: '1px solid #ffe0b2',
              borderRadius: '4px',
            }}>
              {(finding.live_value ?? finding.current_value) || <span style={{ color: '#ccc', fontStyle: 'italic' }}>empty</span>}
            </div>
            {finding.live_value != null && finding.live_value !== finding.current_value && (
              <div style={{ fontSize: '0.85em', color: '#888', marginTop: '0.3em' }}>
                At finding time: <span style={{ textDecoration: 'line-through' }}>{finding.current_value}</span>
                {' '}— the translation has since changed.
              </div>
            )}
          </div>

          {/* Finding */}
          <div style={{ marginBottom: '1.2em' }}>
            <div style={{ fontSize: '0.8em', textTransform: 'uppercase', color: '#888', letterSpacing: '0.05em', marginBottom: '0.3em' }}>
              Finding
            </div>
            <div style={{
              fontSize: '1.05em', lineHeight: '1.5',
              padding: '0.5em 0.7em',
              background: '#f5f5f5',
              borderRadius: '4px',
              borderLeft: '3px solid #f2711c',
            }}>
              {finding.finding}
            </div>
          </div>

          {/* AI Suggestion */}
          {finding.suggested_fix && (
            <div style={{ marginBottom: '1.2em' }}>
              <div style={{ fontSize: '0.8em', textTransform: 'uppercase', color: '#888', letterSpacing: '0.05em', marginBottom: '0.3em' }}>
                AI Suggestion <span style={{ textTransform: 'none', fontStyle: 'italic' }}>(hint only)</span>
              </div>
              <div style={{
                fontSize: '1em', color: '#555',
                padding: '0.5em 0.7em',
                background: '#f8f8f8',
                borderRadius: '4px',
                border: '1px dashed #ccc',
              }}>
                {finding.suggested_fix}
              </div>
            </div>
          )}

          {/* Context panel */}
          <ContextPanel context={context} language={language} />

          {/* --- Actions (hidden for read-only users) --- */}
          {readOnly ? (
            <div style={{ borderTop: '2px solid #e8e8e8', paddingTop: '1em', marginTop: '1em', textAlign: 'center', color: '#999' }}>
              <Icon name="lock" /> Read-only mode
            </div>
          ) : (
          <div style={{ borderTop: '2px solid #e8e8e8', paddingTop: '1em', marginTop: '1em' }}>
            {/* Reclassify — always visible */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.8em',
              marginBottom: '1em', flexWrap: 'wrap',
            }}>
              <span style={{ fontSize: '0.85em', color: '#888' }}>
                Reclassify:
              </span>
              <Dropdown
                selection
                compact
                options={[
                  { key: '', value: '', text: `Keep "${finding.error_type_label || finding.error_type}"` },
                  ...ERROR_TYPE_OPTIONS.filter((o) => o.value && o.value !== finding.error_type),
                ]}
                value={reclassifyType}
                onChange={(e, { value }) => setReclassifyType(value)}
                placeholder={`Keep "${finding.error_type_label || finding.error_type}"`}
                style={{ minWidth: '220px' }}
              />
              {reclassifyType && (
                <>
                  <span style={{ fontSize: '0.85em', color: '#2185d0' }}>
                    {finding.error_type_label || finding.error_type} &rarr; {ERROR_TYPE_OPTIONS.find(o => o.value === reclassifyType)?.text}
                  </span>
                  <Button
                    size="small"
                    color="blue"
                    compact
                    loading={acting}
                    disabled={acting}
                    onClick={handleReclassifyOnly}
                  >
                    Save
                  </Button>
                </>
              )}
            </div>

            {showCorrectionField ? (
              <div>
                <div style={{ fontSize: '0.8em', textTransform: 'uppercase', color: '#888', letterSpacing: '0.05em', marginBottom: '0.3em' }}>
                  Corrected Translation
                </div>
                <textarea
                  value={correction}
                  onChange={(e) => setCorrection(e.target.value)}
                  placeholder={`Type the correct ${langName} translation...`}
                  rows={3}
                  style={{
                    width: '100%', fontSize: '1.1em',
                    padding: '0.5em 0.7em',
                    border: '2px solid #2185d0',
                    borderRadius: '4px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                  }}
                />
                <div style={{ display: 'flex', gap: '0.6em', marginTop: '0.5em' }}>
                  <Button
                    color="blue"
                    loading={acting}
                    disabled={acting || !correction.trim()}
                    onClick={handleApplyCorrection}
                  >
                    <Icon name="save" /> Apply Correction
                  </Button>
                  <Button basic onClick={() => { setShowCorrectionField(false); setCorrection(''); }}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '0.8em', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  color="blue"
                  size="large"
                  onClick={() => {
                    setShowCorrectionField(true);
                    setCorrection((finding.live_value ?? finding.current_value) || '');
                  }}
                >
                  <Icon name="pencil" /> Fix Translation
                </Button>
                <Button
                  basic color="green" size="large"
                  loading={acting} disabled={acting}
                  onClick={() => handleDisposition('acceptable')}
                >
                  <Icon name="check" /> Not an Issue
                </Button>
              </div>
            )}
          </div>
          )}
        </Segment>
      )}

      {/* --- Finding List --- */}
      {findingList.length > 0 && (
        <Segment style={{ marginTop: '1.5em' }}>
          <Header as="h4" style={{ marginBottom: '0.5em' }}>
            <Icon name="list" /> All Findings ({findingList.length})
          </Header>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <Table compact size="small" selectable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell style={{ width: '50px' }}>Msg</Table.HeaderCell>
                  <Table.HeaderCell>English</Table.HeaderCell>
                  <Table.HeaderCell>Current Translation</Table.HeaderCell>
                  <Table.HeaderCell style={{ width: '100px' }}>Type</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {findingList.map((f) => {
                  const isActive = f.id === currentId;
                  return (
                    <Table.Row
                      key={f.id}
                      active={isActive}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setCurrentId(f.id)}
                      {...(isActive ? { ref: activeRowRef } : {})}
                    >
                      <Table.Cell><strong>{f.message_id}</strong></Table.Cell>
                      <Table.Cell style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {f.english_source}
                      </Table.Cell>
                      <Table.Cell style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {f.live_value ?? f.current_value}
                      </Table.Cell>
                      <Table.Cell>
                        <Label size="mini">{f.error_type_label || f.error_type}</Label>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          </div>
        </Segment>
      )}
    </Container>
  );
}

/** Collapsible context panel */
const ContextPanel = ({ context, language }) => {
  const [open, setOpen] = useState(false);
  if (!context) return null;

  const { conventions, standard_translations, preserved_terms, siblings, terminology } = context;
  const hasContext = (standard_translations?.length > 0) || (preserved_terms?.length > 0) ||
    (siblings?.length > 0) || (terminology?.length > 0) || conventions;
  if (!hasContext) return null;

  return (
    <div style={{ marginTop: '0.5em' }}>
      <div
        onClick={() => setOpen(!open)}
        style={{ cursor: 'pointer', fontSize: '0.85em', color: '#2185d0', display: 'flex', alignItems: 'center', gap: '0.3em' }}
      >
        <Icon name={open ? 'chevron down' : 'chevron right'} size="small" />
        Reference Context
        {siblings?.length > 0 && <Label size="mini" circular>{siblings.length} siblings</Label>}
        {terminology?.length > 0 && <Label size="mini" circular color="teal">{terminology.length} terms</Label>}
      </div>
      {open && (
        <div style={{ marginTop: '0.5em', fontSize: '0.9em' }}>
          {conventions && (
            <div style={{ marginBottom: '0.8em' }}>
              <strong>Conventions:</strong>{' '}
              {conventions.register && <span>Register: {conventions.register}. </span>}
              {conventions.button_style && <span>Buttons: {conventions.button_style}. </span>}
              {conventions.notes && <div style={{ color: '#666', marginTop: '0.2em', whiteSpace: 'pre-line' }}>{conventions.notes}</div>}
            </div>
          )}
          {preserved_terms?.length > 0 && (
            <div style={{ marginBottom: '0.8em' }}>
              <Label color="purple" size="mini"><Icon name="lock" /> Preserved term — keep in English</Label>
            </div>
          )}
          {standard_translations?.length > 0 && (
            <div style={{ marginBottom: '0.8em' }}>
              <strong>Standard translation:</strong>
              {standard_translations.map((st, i) => (
                <div key={i} style={{ marginLeft: '1em' }}>
                  &ldquo;{st.translation}&rdquo; {st.context && `(${st.context})`}
                  {st.source && <span style={{ color: '#888' }}> — {st.source}</span>}
                </div>
              ))}
            </div>
          )}
          {terminology?.length > 0 && (
            <div style={{ marginBottom: '0.8em' }}>
              <strong>Terminology:</strong>
              {terminology.map((t, i) => (
                <div key={i} style={{ marginLeft: '1em' }}>
                  &ldquo;{t.translation}&rdquo; ({t.source}, reliability: {t.reliability})
                </div>
              ))}
            </div>
          )}
          {siblings?.length > 0 && (
            <Table compact size="small" basic="very">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Language</Table.HeaderCell>
                  <Table.HeaderCell>Translation</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {siblings.map((s, i) => (
                  <Table.Row key={i}>
                    <Table.Cell>
                      <strong>{s.language}</strong>
                      {s.language_name_english && <span style={{ color: '#888' }}> ({s.language_name_english})</span>}
                    </Table.Cell>
                    <Table.Cell>{s.msgstr}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </div>
      )}
    </div>
  );
};
